import React from "react";
import { useSelector } from "react-redux";
import cx from "classnames";

import SwitchButton from "src/components/switchButton";
import {
  selectDomains,
  selectStoryboard,
} from "src/redux/selectors/experiences/storyboardSettings";
import { STORYBOARD_TYPES } from "src/types/experiences/storyboardSettings/storyboard";
import { DomainResponse } from "src/types/experiences/storyboardSettings";
import { isInteractiveContent } from "src/pages/experiences/storyboardSettings/utils/validation";

import Card from "../card";
import TruncateWithTooltip from "src/components/truncateWithTooltip";
import { useDraft } from "../useDraft";
import { canEdit } from "src/pages/experiences/storyboardSettings/utils/permissions";
import {
  mapServerErrorToToast,
  validateAlias,
} from "src/pages/experiences/storyboardSettings/utils/validation";
import {
  toastFailure,
  toastSuccess,
} from "src/pages/experiences/storyboardSettings/utils/notifications";
import type { WebsiteSettingsActions } from "../index";
import shared from "../siteSettings.module.scss";
import styles from "./siteUrlCard.module.scss";
import { DropDown } from "../dropdown";

interface SiteUrlCardProps {
  actions: WebsiteSettingsActions;
}

const SiteUrlCard: React.FC<SiteUrlCardProps> = ({ actions }) => {
  const { draft, setField } = useDraft();
  const storyboard = useSelector(selectStoryboard);
  const fetchedDomains = useSelector(selectDomains);
  const editable = canEdit(draft?.role);

  const currentDomain: DomainResponse | undefined =
    (draft?.domain as DomainResponse | undefined) ??
    (storyboard?.domain as DomainResponse | undefined);

  const domains: DomainResponse[] = (() => {
    if (!currentDomain) return fetchedDomains;
    return fetchedDomains.some((d) => d.id === currentDomain.id)
      ? fetchedDomains
      : [currentDomain, ...fetchedDomains];
  })();

  const selectedDomain: DomainResponse | undefined =
    domains.find((d) => d.id === draft?.domain?.id) ?? domains[0];

  const isInteractive = isInteractiveContent(draft?.type);
  const supportsPageSeek = draft?.type === STORYBOARD_TYPES.INTERACTIVE_CONTENT;
  const supportsStoryboardSeek =
    draft?.type === STORYBOARD_TYPES.INTERACTIVE_CONTENT_V2;
  const supportsSeek =
    isInteractive && (supportsPageSeek || supportsStoryboardSeek);
  const originalDomainId = storyboard?.domain?.id;
  const domainChanged =
    originalDomainId !== undefined &&
    selectedDomain?.id !== undefined &&
    originalDomainId !== selectedDomain.id;

  const aliasValue = draft?.page?.canonical ?? "";
  const aliasError = aliasValue ? validateAlias(aliasValue, draft?.type) : null;

  const handleDomainSelect = (key: string) => {
    if (!editable) return;
    setField(
      "domain",
      domains.find((domain) => domain.id === key),
    );
  };

  const isIndexing = supportsPageSeek
    ? actions.isIndexingPage
    : actions.isIndexingStoryboard;

  const handleSeekToggle = () => {
    if (!editable || !draft) return;
    const next = !draft.page?.seekEnabled;
    setField("page", {
      ...(draft.page as NonNullable<typeof draft>["page"]),
      seekEnabled: next,
    } as NonNullable<typeof draft>["page"]);

    const pageId = draft.page?.id;
    if (!draft.type || !draft.id) return;

    const onSuccess = () =>
      toastSuccess(next ? "Indexed for seek" : "Seek disabled");
    const onError = (error: unknown) =>
      toastFailure(mapServerErrorToToast(error));

    if (supportsPageSeek) {
      // INTERACTIVE_CONTENT (v1): page-level seek indexing
      if (!pageId) return;
      actions.indexPage(
        {
          contentType: "page",
          content: pageId,
          contentMapping: null,
          storyboard: draft.id,
          storyboardType: draft.type,
          addStoryboard: next,
        },
        { onSuccess, onError },
      );
    } else {
      actions.indexStoryboard(
        {
          contentType: "storyboard",
          content: draft.id,
          contentMapping: null,
          storyboard: draft.id,
          storyboardType: draft.type,
          addStoryboard: next,
        },
        { onSuccess, onError },
      );
    }
  };

  return (
    <Card
      title="Site url"
      description="Get a branded domain so visitors can easily find your site"
    >
      <div className={styles.urlContainer}>
        <div className={shared.fieldLabel}>Your vanity URL</div>
        <div className={styles.urlRow}>
          <div
            className={cx(styles.urlDropdown, {
              [styles.urlDropdownFull]: !isInteractive,
            })}
          >
            <DropDown
              icon="paperflite-web"
              disabled={!editable}
              hideWhenSingleOption
              unconstrainedWidth
              matchToggleWidth={!isInteractive}
              selected={{
                key: selectedDomain?.id ?? "",
                label: selectedDomain?.url ?? "",
              }}
              options={domains.map((domain: { id: string; url: string }) => ({
                key: domain.id,
                label: domain.url,
              }))}
              onChange={handleDomainSelect}
            />
          </div>

          {isInteractive && (
            <>
              <div className={styles.urlDivider}></div>

              <div
                className={cx(styles.urlPill, {
                  [styles.urlPillError]: aliasError,
                  [styles.urlPillDisabled]: !editable,
                })}
              >
                {editable ? (
                  <input
                    type="text"
                    className={styles.aliasInput}
                    placeholder="Enter storyboard alias"
                    value={
                      aliasValue.startsWith("/") ? aliasValue : `/${aliasValue}`
                    }
                    onChange={(e) => {
                      const canonical = `/${e.target.value.replace(/^\/+/, "")}`;
                      setField("page", {
                        ...(draft?.page as NonNullable<typeof draft>["page"]),
                        canonical,
                      } as NonNullable<typeof draft>["page"]);
                    }}
                  />
                ) : (
                  <TruncateWithTooltip
                    className={styles.aliasReadonly}
                    placement="top"
                    text={aliasValue}
                    style={{ maxWidth: 300 }}
                  />
                )}
              </div>
            </>
          )}
        </div>
        {aliasError && (
          <div className={styles.aliasErrorText}>{aliasError}</div>
        )}
        {domainChanged && (
          <div className={styles.domainWarning}>
            <i className="paperflite-info" />
            <span>Ensure the domain is verified with search engines.</span>
          </div>
        )}
      </div>
      {isInteractive && (
        <div className={styles.inlineToggleRow}>
          <SwitchButton
            id="excludeFromIndexing"
            label="Exclude from indexing"
            className={cx(shared.switch, {
              [shared.active]: draft?.excludeFromSearch,
              [shared.disabled]: !editable,
            })}
            disabled={!editable}
            checked={draft?.excludeFromSearch || false}
            handleCheck={() =>
              setField("excludeFromSearch", !draft?.excludeFromSearch)
            }
          />
          {supportsSeek && (
            <SwitchButton
              id="indexForSeek"
              label="Index for seek"
              className={cx(shared.switch, {
                [shared.active]: draft?.page?.seekEnabled,
                [shared.disabled]: !editable || isIndexing,
              })}
              disabled={!editable || isIndexing}
              checked={draft?.page?.seekEnabled || false}
              handleCheck={handleSeekToggle}
            />
          )}
        </div>
      )}
    </Card>
  );
};

export default SiteUrlCard;
